import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import dayjs from "dayjs";
import { apiData } from "../services/api_service";
import { listStoreAPI, fetchCustomerDDAPI, fetchSalesPersonsAPI, fetchProductDropdownAPI } from "../config";

// Filter bar shown above the Invoice / CDN / Proforma lists — the same
// Period / From–To / Store / Customer / Salesperson / Search controls the
// dashboard has, so a list can be narrowed (or widened to "All Time") after
// arriving from a dashboard card.
//
// value: { period, fromDate, toDate, storeId, customerId, salesPersonId, search }
// onChange(nextValue) is called with the full new value.

export const PERIOD_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "daily", label: "Today" },
  { value: "weekly", label: "This Week" },
  { value: "monthly", label: "This Month" },
  { value: "yearly", label: "This Year" },
  { value: "custom", label: "Custom range" },
];

// Same windows the dashboard uses (calendar month / year, rolling 7 days).
export const periodToDates = (period) => {
  const today = dayjs().format("YYYY-MM-DD");
  if (period === "daily") return { fromDate: today, toDate: today };
  if (period === "weekly") return { fromDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"), toDate: today };
  if (period === "monthly") return { fromDate: dayjs().startOf("month").format("YYYY-MM-DD"), toDate: today };
  if (period === "yearly") return { fromDate: dayjs().startOf("year").format("YYYY-MM-DD"), toDate: today };
  return { fromDate: "", toDate: "" };
};

// Work out which preset a from/to pair corresponds to (for opening a list from
// a dashboard card with dates already set).
export const inferPeriod = (fromDate, toDate) => {
  if (!fromDate && !toDate) return "all";
  for (const p of ["daily", "weekly", "monthly", "yearly"]) {
    const d = periodToDates(p);
    if (d.fromDate === fromDate && d.toDate === toDate) return p;
  }
  return "custom";
};

export const emptyListFilters = {
  period: "all",
  fromDate: "",
  toDate: "",
  storeId: 0,
  customerId: 0,
  salesPersonId: 0,
  search: "",
  paymentMode: "",
  paymentStatus: "",
  productIds: [],
};

// Payment mode / status choices for reports (showPayment). Modes are the labels
// stored against each payment.
export const PAYMENT_MODE_OPTIONS = [
  { value: "", label: "All Payment Modes" },
  { value: "Cash", label: "Cash" },
  { value: "Card", label: "Card" },
  { value: "Cheque", label: "Cheque" },
  { value: "Bank Transfer", label: "Bank Transfer" },
  { value: "Online Payment", label: "Online Payment" },
];
export const PAYMENT_STATUS_OPTIONS = [
  { value: "", label: "All Payment Statuses" },
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending (unpaid / part-paid)" },
];

const fieldSx = {
  backgroundColor: "#fff",
  borderRadius: 1.5,
  "& .MuiOutlinedInput-root": {
    height: 38,
    borderRadius: 1.5,
    fontSize: "0.78rem",
    fontWeight: 600,
  },
};

const ListFilterBar = ({ value, onChange, onClear, showStore = true, showPayment = false, showDates = true, showSearch = true, showCustomer = true, showSalesPerson = true, showProduct = false }) => {
  const [stores, setStores] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchText, setSearchText] = useState(value.search || "");

  useEffect(() => {
    (async () => {
      try {
        const [s, c, p] = await Promise.all([
          apiData("get", listStoreAPI),
          apiData("get", fetchCustomerDDAPI),
          apiData("get", fetchSalesPersonsAPI),
        ]);
        if (s?.success) setStores(s.data || []);
        const cl = c?.data?.result || (Array.isArray(c?.data) ? c.data : []);
        if (Array.isArray(cl)) setCustomers(cl);
        const pl = p?.data?.data || p?.data || [];
        if (Array.isArray(pl)) setSalesPersons(pl);
      } catch (e) {
        console.error("Failed to load filter options:", e);
      }
    })();
  }, []);

  // "Ordered product" options (only fetched where the filter is shown).
  useEffect(() => {
    if (!showProduct) return;
    (async () => {
      try {
        const r = await apiData("get", `${fetchProductDropdownAPI}?search=`);
        const list = r?.data?.result || [];
        if (Array.isArray(list)) setProducts(list);
      } catch (e) {
        console.error("Failed to load product options:", e);
      }
    })();
  }, [showProduct]);

  // Keep the box in step when the filter is reset from outside.
  useEffect(() => {
    setSearchText(value.search || "");
  }, [value.search]);

  // Debounced search so the list isn't refetched on every keystroke.
  useEffect(() => {
    if (searchText === (value.search || "")) return undefined;
    const t = setTimeout(() => onChange({ ...value, search: searchText }), 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const storeOptions = useMemo(() => [{ id: 0, store_name: "All Stores" }, ...stores], [stores]);
  const customerOptions = useMemo(
    () => [{ customer_id: 0, name: "All Customers / Companies" }, ...customers],
    [customers]
  );
  const spOptions = useMemo(
    () => [{ sales_person_id: 0, employee_name: "All Salespersons" }, ...salesPersons],
    [salesPersons]
  );

  const setPeriod = (p) => {
    if (p === "custom") return onChange({ ...value, period: "custom" });
    onChange({ ...value, period: p, ...periodToDates(p) });
  };
  const setDate = (key, v) =>
    onChange({ ...value, [key]: v, period: "custom" });

  const active =
    value.period !== "all" ||
    Number(value.storeId) > 0 ||
    Number(value.customerId) > 0 ||
    Number(value.salesPersonId) > 0 ||
    !!value.paymentMode ||
    !!value.paymentStatus ||
    (value.productIds || []).length > 0 ||
    !!value.search;

  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      gap={1.25}
      sx={{ p: 1.25, mb: 1.5, borderRadius: 3, backgroundColor: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15,23,42,0.04)" }}
    >
      <Typography sx={{ fontSize: "0.66rem", fontWeight: 800, letterSpacing: 0.8, color: "#94a3b8", textTransform: "uppercase", pr: 0.5 }}>
        Filters
      </Typography>

      {showDates && (<>
      <Autocomplete
        size="small"
        disableClearable
        // "Custom range" is not something to pick — the From / To dates do that. It only
        // appears (as the shown value) when the chosen dates don't match a preset.
        options={value.period === "custom" ? PERIOD_OPTIONS : PERIOD_OPTIONS.filter((p) => p.value !== "custom")}
        getOptionLabel={(o) => o.label}
        value={PERIOD_OPTIONS.find((p) => p.value === value.period) || PERIOD_OPTIONS[0]}
        onChange={(e, v) => v && setPeriod(v.value)}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        renderInput={(params) => <TextField {...params} placeholder="Period" sx={{ ...fieldSx, width: 150 }} />}
      />

      <TextField
        size="small"
        type="date"
        label="From"
        InputLabelProps={{ shrink: true }}
        value={value.fromDate || ""}
        onChange={(e) => setDate("fromDate", e.target.value)}
        sx={{ ...fieldSx, width: 150 }}
      />
      <TextField
        size="small"
        type="date"
        label="To"
        InputLabelProps={{ shrink: true }}
        value={value.toDate || ""}
        onChange={(e) => setDate("toDate", e.target.value)}
        sx={{ ...fieldSx, width: 150 }}
      />
      </>)}

      {showStore && (
        <Autocomplete
          size="small"
          disableClearable
          options={storeOptions}
          getOptionLabel={(o) => o.store_name || ""}
          value={storeOptions.find((s) => Number(s.id) === Number(value.storeId)) || storeOptions[0]}
          onChange={(e, v) => onChange({ ...value, storeId: v ? v.id : 0 })}
          isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
          renderInput={(params) => <TextField {...params} placeholder="Store" sx={{ ...fieldSx, width: 170 }} />}
        />
      )}

      {showCustomer && (
      <Autocomplete
        size="small"
        disableClearable
        options={customerOptions}
        getOptionLabel={(o) => o.name || ""}
        value={customerOptions.find((c) => Number(c.customer_id) === Number(value.customerId)) || customerOptions[0]}
        onChange={(e, v) => onChange({ ...value, customerId: v ? v.customer_id : 0 })}
        isOptionEqualToValue={(o, v) => Number(o.customer_id) === Number(v.customer_id)}
        renderInput={(params) => <TextField {...params} placeholder="Customer / Company" sx={{ ...fieldSx, width: 210 }} />}
      />
      )}

      {showSalesPerson && (
      <Autocomplete
        size="small"
        disableClearable
        options={spOptions}
        getOptionLabel={(o) => o.employee_name || ""}
        value={spOptions.find((p) => Number(p.sales_person_id) === Number(value.salesPersonId)) || spOptions[0]}
        onChange={(e, v) => onChange({ ...value, salesPersonId: v ? v.sales_person_id : 0 })}
        isOptionEqualToValue={(o, v) => Number(o.sales_person_id) === Number(v.sales_person_id)}
        renderInput={(params) => <TextField {...params} placeholder="Salesperson" sx={{ ...fieldSx, width: 190 }} />}
      />
      )}

      {showProduct && (
        <Autocomplete
          multiple
          size="small"
          limitTags={1}
          disableCloseOnSelect
          options={products}
          getOptionLabel={(o) => `${o.product_name || ""}${o.sku ? ` (${o.sku})` : ""}`}
          value={products.filter((p) => (value.productIds || []).map(Number).includes(Number(p.id)))}
          onChange={(e, v) => onChange({ ...value, productIds: v.map((p) => Number(p.id)) })}
          isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
          renderInput={(params) => (
            <TextField {...params} placeholder={(value.productIds || []).length ? "" : "Ordered product(s)"} sx={{ ...fieldSx, width: 260, "& .MuiOutlinedInput-root": { ...fieldSx["& .MuiOutlinedInput-root"], height: "auto", minHeight: 38 } }} />
          )}
        />
      )}

      {showPayment && (
        <>
          <Autocomplete
            size="small"
            disableClearable
            options={PAYMENT_MODE_OPTIONS}
            getOptionLabel={(o) => o.label}
            value={PAYMENT_MODE_OPTIONS.find((o) => o.value === (value.paymentMode || "")) || PAYMENT_MODE_OPTIONS[0]}
            onChange={(e, v) => onChange({ ...value, paymentMode: v ? v.value : "" })}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            renderInput={(params) => <TextField {...params} placeholder="Payment Mode" sx={{ ...fieldSx, width: 180 }} />}
          />
          <Autocomplete
            size="small"
            disableClearable
            options={PAYMENT_STATUS_OPTIONS}
            getOptionLabel={(o) => o.label}
            value={PAYMENT_STATUS_OPTIONS.find((o) => o.value === (value.paymentStatus || "")) || PAYMENT_STATUS_OPTIONS[0]}
            onChange={(e, v) => onChange({ ...value, paymentStatus: v ? v.value : "" })}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            renderInput={(params) => <TextField {...params} placeholder="Payment Status" sx={{ ...fieldSx, width: 220 }} />}
          />
        </>
      )}

      {showSearch && (
      <TextField
        size="small"
        placeholder="Search number, customer, phone, email…"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, color: "#94a3b8", mr: 0.5 }} /> }}
        sx={{ ...fieldSx, width: 260 }}
      />
      )}

      {active && (
        <Button size="small" startIcon={<ClearIcon />} onClick={onClear} sx={{ textTransform: "none", fontWeight: 600 }}>
          Reset
        </Button>
      )}
    </Box>
  );
};

export default ListFilterBar;
